import React from "react";
import { useNavigate } from "react-router-dom";
 import { Helmet } from "react-helmet";
import {
  FaArrowLeft,
  FaLock,
  FaChevronDown,
  FaDesktop,
  FaHistory,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { useTranslation } from "../../context/useTranslation";
import { useDarkMode } from "../../hooks/useDarkMode";

import DeviceManagement from "./AccountInfoPages/DeviceManagement";
import LoginHistory from "./AccountInfoPages/LoginHistory";
import SecuritySettings from "./AccountInfoPages/SecuritySettings";

const SettingsPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = React.useState(null);

  const settings = [
    {
      id: 0,
      icon: <FaLock />,
      title: "Security & Privacy",
      description: "Protect your account and data",
      color: "red",
      component: <SecuritySettings />,
    },
    {
      id: 1,
      icon: <FaDesktop />,
      title: "Devices",
      description: "Manage your connected devices",
      color: "green",
      component: <DeviceManagement />,
    },
    {
      id: 2,
      icon: <FaHistory />,
      title: "Login History",
      description: "View all your recent logins",
      color: "purple",
      component: <LoginHistory />,
    },
  ];

  return (
    <>
   

<Helmet>

  {/* Title */}
  <title>Account Settings | Seo Up Rank Dashboard</title>

  {/* Description */}
  <meta
    name="description"
    content="Manage your Seo Up Rank account settings, profile preferences, security options and dashboard configuration."
  />

  {/* Private page - Do not index */}
  <meta name="robots" content="noindex, nofollow" />

  {/* Canonical */}
  <link rel="canonical" href="https://novarsis.ai/settings" />

  {/* Open Graph */}
  <meta property="og:title" content="Seo Up Rank Account Settings" />
  <meta
    property="og:description"
    content="Update your Seo Up Rank account preferences, profile details and security settings from the dashboard."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://novarsis.ai/settings" />
  <meta property="og:site_name" content="Seo Up Rank" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content="Seo Up Rank Settings" />
  <meta
    name="twitter:description"
    content="Manage your Seo Up Rank account settings and dashboard preferences."
  />

  {/* Schema */}
  <script type="application/ld+json">
    {`
    {
      "@context":"https://schema.org",
      "@type":"WebPage",
      "name":"Settings - Seo Up Rank",
      "url":"https://novarsis.ai/settings",
      "description":"User account settings page for managing preferences and security in Seo Up Rank dashboard."
    }
    `}
  </script>

</Helmet>
   
    <div
      className={`w-full max-w-full overflow-x-hidden min-h-screen px-3 sm:px-6 py-6 ${
        isDarkMode ? "bg-[#0f1419]" : "bg-gray-50"
      }`}
    >
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-start gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className={`p-2 rounded-lg ${
              isDarkMode
                ? "text-gray-400 hover:bg-gray-800"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <FaArrowLeft />
          </button>

          <div className="w-full">
            <h1
              className={`text-2xl sm:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {t("settings") || "Settings"}
            </h1>
            <p
              className={`${isDarkMode ? "text-gray-400" : "text-gray-600"} text-sm`}
            >
              Manage your account preferences and security
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="space-y-4">
          {settings.map((setting) => (
            <div key={setting.id} className="w-full">
              {/* Card */}
              <button
                onClick={() =>
                  setOpenIndex(openIndex === setting.id ? null : setting.id)
                }
                className={`w-full p-4 sm:p-6 rounded-xl border text-left transition-colors ${
                  isDarkMode
                    ? "bg-[#1a1f2e] border-[#2a2f3e] hover:bg-[#1f2433]"
                    : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center justify-between gap-3 w-full">
                  <div className="flex items-center gap-3 min-w-0 w-full">
                    <div
                      className={`p-3 rounded-lg shrink-0 ${
                        setting.id === 0
                          ? isDarkMode
                            ? "bg-red-500/10 text-red-400"
                            : "bg-red-500/10 text-red-600"
                          : setting.id === 1
                            ? isDarkMode
                              ? "bg-green-500/10 text-green-400"
                              : "bg-green-500/10 text-green-600"
                            : isDarkMode
                              ? "bg-purple-500/10 text-purple-400"
                              : "bg-purple-500/10 text-purple-600"
                      }`}
                    >
                      {setting.icon}
                    </div>

                    <div className="min-w-0">
                      <h2
                        className={`font-bold text-base sm:text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {setting.title}
                      </h2>

                      <p
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        {setting.description}
                      </p>
                    </div>
                  </div>

                  <motion.div
                    animate={{ rotate: openIndex === setting.id ? 180 : 0 }}
                    className={
                      setting.id === 0
                        ? isDarkMode
                          ? "text-red-400"
                          : "text-red-600"
                        : setting.id === 1
                          ? isDarkMode
                            ? "text-green-400"
                            : "text-green-600"
                          : isDarkMode
                            ? "text-purple-400"
                            : "text-purple-600"
                    }
                  >
                    <FaChevronDown />
                  </motion.div>
                </div>
              </button>

              {/* Content */}
              {openIndex === setting.id && (
                <div className="w-full overflow-hidden border-l-4 border-green-500 pl-4 py-4">
                  <div className="w-full max-w-full">{setting.component}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div> </>
  );
};

export default SettingsPage;
